import type { CardDef, CardTraits } from '../../core/card';
import { cardTitle, effectId } from '../../core/ids';
import type { Lore, LoreTerm } from '../../core/lore';
import type { TextCard, TextTerm } from '../../i18n/text';
import type { ContentPack } from '../../core/pack';
import { coreEffects } from './effects';
import { VANILLA_PACK_ID } from './id';

export { VANILLA_PACK_ID };

/**
 * 原版包：爆炸猫 Original Edition（56 张）。
 *
 * 这是唯一的必需包。牌名沿用换皮后的编程梗（`docs/archive/old-repo-review.md` §2.6），
 * 数量与行为对照官方规则书 `EK_OE_Instructions_14JAN22`（`docs/rules.md` §2）。
 *
 * ⚠️ 加扩展包时**不要改这个文件**，新建一个并列的包即可。
 */
interface CardSpec {
  /** 包内局部名，会拼成 `vanilla:<local>`。⚠️ 一律 ASCII 英文 */
  local: string;
  /** 中文显示名 */
  name: string;
  /** 英文显示名（见 `CardDef.nameEn`）；缺省时回退中文 */
  nameEn: string;
  /** 印在牌面上的一句短文案 */
  description: string;
  /** 印在牌面上的英文短文案（见 `CardDef.descriptionEn`） */
  descriptionEn: string;
  /** 悬停说明里的详细效果（比 `description` 长，见 `CardDef.lore`） */
  lore: Lore;
  /** 悬停说明的英文详细效果（见 `CardDef.loreEn`） */
  loreEn: Lore;
  /** 官方预设里放几张 */
  count: number;
  traits?: CardTraits;
  /** 主动效果；省略或 null = 没有主动效果 */
  effect?: string | null;
}

function defineCard(spec: CardSpec): CardDef {
  return {
    id: cardTitle(VANILLA_PACK_ID, spec.local),
    pack: VANILLA_PACK_ID,
    local: spec.local,
    name: spec.name,
    nameEn: spec.nameEn,
    description: spec.description,
    descriptionEn: spec.descriptionEn,
    lore: spec.lore,
    loreEn: spec.loreEn,
    traits: spec.traits ?? {},
    effect: spec.effect === undefined || spec.effect === null ? null : effectId(VANILLA_PACK_ID, spec.effect),
    defaultCount: spec.count,
  };
}

/** 无效牌：单独打出没有任何效果，只能用来凑连击 */
const POWERLESS: CardTraits = { powerless: true };

/** lore 里的**牌引用**：只存局部名，显示名随语言/改名自动走（见 `core/lore.ts`） */
const ref = (local: string): TextCard => ({ type: 'card', cardId: cardTitle(VANILLA_PACK_ID, local) });
/** lore 里的**固定术语**：按当前语言查词，保证同一个概念永远同一个词 */
const term = (t: LoreTerm): TextTerm => ({ type: 'term', term: t });

const powerlessLore: Lore = {
  effect: ['无法被单独打出。'],
  // 连击规则**不在这里**：那是房间村规，按是否启用动态插在"功能"之后，
  // 见 core/lore.ts 的 comboRuleExtras。
  hint: [
    '（拖拽',
    term('hand'),
    '可以调换',
    term('hand'),
    '顺序，迷惑你的对手）',
  ],
};

const powerlessLoreEn: Lore = {
  effect: ['Cannot be played on its own.'],
  hint: [
    '(Drag cards in your ',
    term('hand'),
    ' to reorder them and confuse your opponents)',
  ],
};

const cards: readonly CardDef[] = [
  // ============================================================
  // 会死人的牌
  // ============================================================
  defineCard({
    local: 'Exception',
    name: '异常',
    nameEn: 'Exception',
    description: '道途的终点处。',
    descriptionEn: 'The end of the path.',
    lore: {
      effect: [
        '无法被打断，抽到时造成',
        term('nearDeath'),
        '，仅会在令一位玩家出局后进入',
        term('discardPile'),
        '。',
      ],
      strategy: [
        '整场游戏的目标，便是使用各类功能牌改变牌序，将它送入其他玩家的',
        term('hand'),
        '，杀死你的所有对手。但当你抽到它时，只能使用',
        ref('Catch'),
        '规避死亡。',
      ],
    },
    loreEn: {
      effect: [
        'Cannot be interrupted. Drawing it causes ',
        term('nearDeath'),
        ', and it only enters the ',
        term('discardPile'),
        ' after eliminating a player.',
      ],
      strategy: [
        "The goal of the whole game is to use action cards to change the deck order, slip it into another player's ",
        term('hand'),
        ", and kill all your opponents. But when you draw it, only ",
        ref('Catch'),
        ' can save you from death.',
      ],
    },
    count: 4,
    // 不能被主动打出；可被 Catch 救；且**永远不会待在某人的手里**
    traits: { lethal: { defusable: true }, playable: false, neverInHand: true },
  }),

  // ============================================================
  // 救命牌
  // ============================================================
  defineCard({
    local: 'Catch',
    name: '捕获',
    nameEn: 'Catch',
    description: '系紧断裂的呼吸，抓紧我。',
    descriptionEn: 'Bind the broken breath; hold on to me.',
    lore: {
      effect: [
        '脱离',
        term('nearDeath'),
        '状态的唯一手段，一种',
        term('hand'),
        '形式的生命值。',
      ],
      strategy: [
        '打出后将',
        ref('Exception'),
        '放回',
        term('drawPile'),
        '的任意位置，不会被打断。\n十分宝贵，设法从',
        term('drawPile'),
        '或其他玩家的',
        term('hand'),
        '中抽得此牌以增加你胜利的概率\n同时，其他玩家也会试图窃取你手中的',
        ref('Catch'),
        '，请务必小心。',
      ],
    },
    loreEn: {
      effect: [
        'The only way out of ',
        term('nearDeath'),
        '; a life point in the form of a card.',
      ],
      strategy: [
        'Playing it puts ',
        ref('Exception'),
        ' back anywhere in the ',
        term('drawPile'),
        ", and it cannot be interrupted.\nIt is precious—try to draw it from the ",
        term('drawPile'),
        " or another player's ",
        term('hand'),
        ' to raise your odds of winning.\nOther players will also try to steal the ',
        ref('Catch'),
        ' in your ',
        term('hand'),
        ', so be careful.',
      ],
    },
    count: 6,
    // 只能在致命结算时打出，不占出牌阶段
    traits: { rescue: true, playable: false },
  }),

  // ============================================================
  // 功能牌
  // ============================================================
  defineCard({
    local: 'SigTrap',
    name: '断点',
    nameEn: 'SIGTRAP',
    description: '肢体将死去的语言紧紧搂在怀中。',
    descriptionEn: 'The body clutches a dead language to its chest.',
    lore: {
      effect: [
        '打断除',
        ref('Catch'),
        '或',
        ref('Exception'),
        '外任意行动。',
      ],
      strategy: [
        '可以打断其他',
        ref('SigTrap'),
        '，以保护行动不被打断。\n但将',
        ref('SigTrap'),
        '打断后仍可以再次打断行动，直至',
        ref('SigTrap'),
        '被耗尽为止。\n获取此牌以保护自己的行动，或阻止其他玩家的行动。\n',
      ],
    },
    loreEn: {
      effect: [
        'Interrupts any action except ',
        ref('Catch'),
        ' or ',
        ref('Exception'),
        '.',
      ],
      strategy: [
        'You can interrupt another ',
        ref('SigTrap'),
        ' to protect an action from being interrupted.\nBut once a ',
        ref('SigTrap'),
        ' is interrupted, it can still interrupt actions again until the ',
        ref('SigTrap'),
        "s run out.\nGet this card to protect your own actions, or to stop other players' actions.\n",
      ],
    },
    count: 5,
    // ⚠️ playable: false —— 只能在响应窗口里打，不能当普通牌打出
    //（见 docs/rules.md §9.12：没有东西可取消时，打它没有任何意义）。
    // 但它仍然可以当连击材料（官方："ANY pair of cards with the same title"）。
    traits: { interrupt: true, playable: false },
    effect: null,
  }),
  defineCard({
    local: 'Signal',
    name: '信号',
    nameEn: 'Signal',
    description: '终止，核心已转储。',
    descriptionEn: 'Terminated; core dumped.',
    lore: {
      effect: [
        '不抽牌，立即结束',
        term('turn'),
        '，令下一位玩家连续进行两个',
        term('turn'),
        '。',
      ],
      strategy: [
        '当你确信接下来的牌中有',
        ref('Exception'),
        '时，可以使用',
        ref('Signal'),
        '让对方更加难以处理。\n但如果对方没有抽到',
        ref('Exception'),
        '，可能反而使对方获得更多',
        term('hand'),
        '，增加其获胜机会。\n',
      ],
    },
    loreEn: {
      effect: [
        'Draw no cards and end your ',
        term('turn'),
        ' immediately, making the next player take two ',
        term('turn'),
        's in a row.',
      ],
      strategy: [
        'When you are sure an ',
        ref('Exception'),
        ' is coming up, use ',
        ref('Signal'),
        ' to make it harder for the next player to deal with.\nBut if they do not draw the ',
        ref('Exception'),
        ', you may instead give them more cards and a better chance to win.\n',
      ],
    },
    count: 4,
    effect: 'signal',
  }),
  defineCard({
    local: 'Suspend',
    name: '挂起',
    nameEn: 'Suspend',
    description: '还没有轮到我们醒来。',
    descriptionEn: 'It is not yet our turn to wake.',
    lore: {
      effect: [
        '不抽牌，立即结束',
        term('turn'),
        '。',
      ],
      strategy: [
        '当你确信接下来的牌中有',
        ref('Exception'),
        '时，可以使用',
        ref('Suspend'),
        '跳过',
        term('turn'),
        '，以更低的成本规避死亡。\n但你也会因此失去获取',
        term('hand'),
        '的机会，若下一张不是',
        ref('Exception'),
        '，这可能令你陷入劣势。\n',
      ],
    },
    loreEn: {
      effect: [
        'Draw no cards and end your ',
        term('turn'),
        ' immediately.',
      ],
      strategy: [
        'When you are sure an ',
        ref('Exception'),
        ' is coming up, use ',
        ref('Suspend'),
        ' to skip your ',
        term('turn'),
        ' and dodge death at a lower cost.\nBut you also lose the chance to gain cards; if the next card is not an ',
        ref('Exception'),
        ', this may put you at a disadvantage.\n',
      ],
    },
    count: 4,
    effect: 'suspend',
  }),
  defineCard({
    local: 'Await',
    name: '求值',
    nameEn: 'Await',
    description: '根据计划，现在需要将此处堆满。',
    descriptionEn: 'As planned, this place must now be piled full.',
    lore: {
      effect: [
        '指定一名在场玩家，要求对方自选一张',
        term('hand'),
        '给你。',
      ],
      strategy: [
        '一般而言，对方会给你塞废牌减少损失，\n但当对方',
        term('hand'),
        '较少，你',
        term('hand'),
        '较多时，也可能帮助你凑出',
        term('pair'),
        '或',
        term('triple'),
        '。\n合理利用',
        ref('Await'),
        '，',
        term('pair'),
        '和',
        term('triple'),
        '持续压制，可以大量夺取',
        term('hand'),
        '，令对手陷入大劣势。',
      ],
    },
    loreEn: {
      effect: [
        'Choose a player and ask them to give you a card of their choice.',
      ],
      strategy: [
        'Usually they will hand you junk to cut their losses,\nbut when they have few cards and you have many, it can still help you build a ',
        term('pair'),
        ' or ',
        term('triple'),
        '.\nUse ',
        ref('Await'),
        ' well and keep up the pressure with pairs and triples to seize a great many cards and leave your opponents at a huge disadvantage.',
      ],
    },
    count: 4,
    traits: { needsTarget: true },
    effect: 'await',
  }),
  defineCard({
    local: 'Hash',
    name: '哈希',
    nameEn: 'Hash',
    description: '将记忆涂满双手，拥抱历史与既定的未来。',
    descriptionEn: 'Smear memory over both hands; embrace history and the future already set.',
    lore: {
      effect: [
        '打乱',
        term('drawPile'),
        '。',
      ],
      strategy: [
        '当有人正在借助牌序掌控局面，或你怀疑有人将',
        ref('Exception'),
        '放在你将要抽取的这一张时，\n使用这张牌，打乱所有人的算盘。',
      ],
    },
    loreEn: {
      effect: [
        'Shuffles the ',
        term('drawPile'),
        '.',
      ],
      strategy: [
        'When someone is controlling the game through the deck order, or you suspect they placed an ',
        ref('Exception'),
        ' on the card you are about to draw,\nplay this card to upset everyone',
        "'s plans.",
      ],
    },
    count: 4,
    effect: 'hash',
  }),
  defineCard({
    local: 'Debug',
    name: '调试',
    nameEn: 'Debug',
    description: '在阳光穿透脏腑前，展露未融的精致。',
    descriptionEn: 'Before sunlight pierces the organs, reveal the delicacy that has not yet melted.',
    lore: {
      effect: [
        term('drawPile'),
        '中接下来三张牌将变为可见。',
      ],
      strategy: [
        '得到更多信息是更多策略得以施行的基础。\n但这张牌无法改变顺序，需要配合其他手段达成控制。',
      ],
      hint: [
        '（点击',
        term('drawPile'),
        '以查看对你可见的所有情报）',
      ],
    },
    loreEn: {
      effect: [
        'The next three cards in the ',
        term('drawPile'),
        ' become visible.',
      ],
      strategy: [
        'More information is the foundation for more strategies.\nBut this card cannot change the order, so you need other means to gain control.',
      ],
      hint: [
        '(Click the ',
        term('drawPile'),
        ' to see all the intel visible to you)',
      ],
    },
    count: 5,
    effect: 'debug',
  }),

  // ============================================================
  // 无效牌 × 5
  //
  // ⚠️ 内部 ID 用 `Powerless1`…`Powerless5`。两条理由合起来才定下这个名字：
  //   ① 全项目 ID 一律 **ASCII 英文** —— 中文 ID 会在网络报文、日志、存档、
  //      URL 里到处惹麻烦，而且很难 grep。
  //   ② 但它也**不能起花哨的名字**（`Delicacy` 之类）—— 那会让人以为它有效果。
  //      `Powerless` 直说「没有效果」，正好把这条也讲清楚了。
  // 显示名保留原型的风味文案，玩家靠它区分能不能凑一对。
  // ============================================================
  defineCard({
    local: 'Powerless1',
    name: '微妙的菜肴',
    nameEn: 'A Delicate Dish',
    description: '散发着微妙的气味……也不是不能吃，只是，非常不想让人看见！',
    descriptionEn: 'Gives off a subtle smell... not that you cannot eat it, you just really do not want anyone to see!',
    lore: powerlessLore,
    loreEn: powerlessLoreEn,
    count: 4,
    traits: POWERLESS,
  }),
  defineCard({
    local: 'Powerless2',
    name: '囚者颈环',
    nameEn: "Prisoner's Collar",
    description: '没什么值得一提的，除了可能隐约的一丝死老鼠的味道......',
    descriptionEn: 'Nothing worth mentioning, except maybe a faint whiff of dead rat...',
    lore: powerlessLore,
    loreEn: powerlessLoreEn,
    count: 4,
    traits: POWERLESS,
  }),
  defineCard({
    local: 'Powerless3',
    name: '终极奉献',
    nameEn: 'Ultimate Devotion',
    description: '用下界合金锭升级一把锄，然后重新考虑你的人生抉择',
    descriptionEn: 'Upgrade a hoe with a netherite ingot, then reconsider your life choices.',
    lore: powerlessLore,
    loreEn: powerlessLoreEn,
    count: 4,
    traits: POWERLESS,
  }),
  defineCard({
    local: 'Powerless4',
    name: '甘草种子',
    nameEn: 'Licorice Seeds',
    description: '走快些，捡得辛苦，从来不用！',
    descriptionEn: 'Walk faster, gather with effort, and never use it!',
    lore: powerlessLore,
    loreEn: powerlessLoreEn,
    count: 4,
    traits: POWERLESS,
  }),
  defineCard({
    local: 'Powerless5',
    name: '蛇不咬',
    nameEn: "Snake Won't Bite",
    description: '保留。',
    descriptionEn: 'Reserved.',
    lore: powerlessLore,
    loreEn: powerlessLoreEn,
    count: 4,
    traits: POWERLESS,
  }),
];

export const VANILLA_PACK: ContentPack = {
  id: VANILLA_PACK_ID,
  name: '原版包',
  description: '爆炸猫 Original Edition，56 张，2–5 人。',
  required: true,
  cards,
  effects: coreEffects,
};
